import { useContext, useEffect, useState } from "react";
import {
  LocalizationContext,
  PlaceContext,
  ShareLinkObject,
} from "../../lib/context";
import { useRouter } from "next/router";
import { getDistance } from "../../lib/global";
import theme from "../../styles/theme";
import { glassBackground } from "../../styles/globalStyles";
import { Box } from "@mui/material";
import CompareSwitchButton from "../CommonComponents/CompareSwitchButton";
import Image from "next/legacy/image";
import ShareButton from "../ShareButton";
import { ProfilePage } from "../../types/cmsLocalizationTypes";
import { useScreenSize } from "src/hooks/useScreenSize";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ContactDetails from "./ContactDetails";

export function ContactGrid({
  droner,
  localizedPageContent,
  canEdit,
  setInfoOpen,
  infoOpen,
}: {
  droner: any;
  localizedPageContent: ProfilePage;
  canEdit: boolean;
  setInfoOpen: (bol: boolean) => void;
  infoOpen: boolean;
}) {
  const [showMore, setShowMore] = useState(() => false);
  const { shareLinkObject } = useContext(ShareLinkObject);
  const { localeStrings } = useContext(LocalizationContext);
  const smAndDown = useScreenSize("sm", "down");
  const measurement = "km";
  const router = useRouter();

  const [distance, setDistance] = useState(0);
  const { place, setPlace } = useContext(PlaceContext);

  useEffect(() => {
    place &&
      setDistance(
        getDistance(place.lat, place.lng, droner.lat, droner.lng, "km")
      );
  }, [droner.lat, droner.lng, place]);

  return (
    <Box
      className="profile__contact"
      sx={{
        [theme.breakpoints.up("sm")]: {
          ...glassBackground,
        },
      }}
    >
      <div className="flex justify-content-space-between w-full align-items-center">
        {smAndDown ? (
          <KeyboardArrowLeftIcon onClick={() => router.back()} />
        ) : (
          <span
            className="button primary-lighten-2"
            onClick={() => router.back()}
            children={localeStrings.uiGeneral.backButton}
          />
        )}

        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <CompareSwitchButton droner={droner} />
        </Box>
      </div>

      <div className="d-grid gap-normal">
        <div className="profile__contact--avatar">
          <Image
            alt={droner?.username}
            src={droner?.profImage ?? "/assets/profile_placeholder.svg"}
            sizes="(min-width: 100vw) 100vw,
                  (min-width: 100vw) 100vw,
                 100vw"
            layout="fill"
            objectFit="cover"
            quality={50}
          />
        </div>
        <h2
          className={`text-center mt-large flex align-items-center gap-normal ${
            smAndDown ? "headline-4" : "headline-2"
          }`}
        >
          {droner.displayName ?? droner.username}
          <ShareButton linkObj={shareLinkObject} withoutText={true} />
        </h2>
        <div className="flex flex-wrap gap-small justify-content-center">
          {droner.skills?.map((skill: string, i: number) => {
            const locSkills = localeStrings.uiGeneral.skills;
            const locSkill = locSkills.filter((s) => s.skillId === skill)[0]
              ?.name;
            return (
              <div className="skill-tag text-no-wrap" key={i}>
                {locSkill}
              </div>
            );
          })}
        </div>
      </div>

      {smAndDown && (
        <Box className="flex justify-content-space-between w-full mt-large mb-large align-items-center">
          <Box className="flex gap-large">
            {canEdit && (
              <Image
                src="/assets/settingsIcon.png"
                width="22"
                height="22"
                alt="Settings"
                className="pointer"
                onClick={() => router.push("/settings")}
              />
            )}
          </Box>

          <Box className="flex gap-large">
            <Box
              className="subtitle-3 pb-normal pointer"
              sx={{
                borderBottom: infoOpen ? "2px solid black" : "unset",
                color: !infoOpen ? "#b5b5b5" : "",
              }}
              onClick={() => setInfoOpen(true)}
            >
              Profile
            </Box>
            <Box
              className="subtitle-3 pb-normal pointer"
              sx={{
                borderBottom: !infoOpen ? "2px solid black" : "unset",
                color: infoOpen ? "#b5b5b5" : "",
              }}
              onClick={() => setInfoOpen(false)}
            >
              Projects
            </Box>
          </Box>

          <CompareSwitchButton droner={droner} />
        </Box>
      )}
      {infoOpen && smAndDown && (
        <ContactDetails
          droner={droner}
          canEdit={canEdit}
          smAndDown={smAndDown}
          localizedPageContent={localizedPageContent}
          measurement={measurement}
        />
      )}

      {!smAndDown && (
        <ContactDetails
          droner={droner}
          canEdit={canEdit}
          smAndDown={smAndDown}
          localizedPageContent={localizedPageContent}
          measurement={measurement}
        />
      )}
    </Box>
  );
}
